/**
 * PlansPage — Full pre-purchase micro-journey
 * Structured emotional flow: curiosity → understanding → decision
 * Features: scroll-reveal, sticky CTA bar, tier persistence, dynamic CTAs
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Heart, Sparkles, Check, ArrowRight, ChevronDown,
  Lock, Eye, Award, Layers, PenLine, Users,
} from 'lucide-react';
import Header from '../components/shared/Header';

/* ─── scroll-reveal hook ─── */
const useReveal = (opts = {}) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); ob.unobserve(el); } },
      { threshold: opts.threshold ?? 0.15, rootMargin: opts.rootMargin ?? '0px 0px -40px 0px' }
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, []);
  return [ref, vis];
};

/* ─── localStorage persistence (7-day expiry) ─── */
const LS_KEY = 'mc_selection';
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

const loadSelection = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.ts > SEVEN_DAYS) { localStorage.removeItem(LS_KEY); return null; }
    return data;
  } catch { return null; }
};

const saveSelection = (tier, mode) => {
  localStorage.setItem(LS_KEY, JSON.stringify({ tier, mode, ts: Date.now() }));
};

/* ─── tier data ─── */
const TIER_DATA = {
  CORE: {
    key: 'core', price: 97, icon: Shield,
    helper: 'A simple place to begin',
    individual: {
      title: 'Core Clarity',
      tagline: 'Define what matters to you before involving anyone else.',
      bullets: ['Guided self-reflection experience','Clarify your needs, values, and expectations','Build personal relationship clarity','Includes your Marrsing Alignment Score'],
      closeLine: 'Best for starting with yourself.',
      audienceLine: 'Perfect for anyone beginning with personal clarity before partnership.',
      cta: 'Start My Clarity',
    },
    couple: {
      title: 'Core Clarity',
      tagline: 'Build the foundation for clear expectations and healthy boundaries.',
      bullets: ['Guided self-reflection for each partner','Clarify values, needs, and expectations','Prepare for aligned conversations together','Includes your Marrsing Alignment Score'],
      closeLine: 'Essential clarity for any partnership.',
      audienceLine: 'Perfect for dating, engaged, or soon-to-be married couples starting with clarity.',
      cta: 'Start Our Clarity',
    },
  },
  RITUAL: {
    key: 'ritual', price: 197, badge: 'Most Popular', icon: Heart,
    helper: 'Turn clarity into conversation',
    individual: {
      title: 'Ritual Structure',
      tagline: 'Turn self-awareness into intentional relational readiness.',
      bullets: ['Everything in CORE','Reflection prompts that prepare you for deeper dialogue','Structured clarity practices','Ongoing check-in rhythm','Includes your Marrsing Alignment Score'],
      closeLine: 'Where clarity becomes practice.',
      audienceLine: 'Designed for building alignment \u2014 before defining your full agreement.',
      cta: 'Build My Structure',
    },
    couple: {
      title: 'Ritual Structure',
      tagline: 'Turn clarity into meaningful conversations and ongoing alignment.',
      bullets: ['Everything in CORE','The Clarity Conversation\u2122 experience','Guided partner exchange','Ongoing check-in rhythms and accountability','Includes your Marrsing Alignment Score'],
      closeLine: 'Where real relationship transformation begins.',
      audienceLine: 'Designed for couples building alignment \u2014 before defining their full agreement.',
      cta: 'Build Our Alignment',
    },
  },
  LEGACY: {
    key: 'legacy', price: 297, badge: 'Most Transformational', icon: Sparkles,
    helper: 'Define what matters most',
    individual: {
      title: 'Legacy Vision',
      tagline: 'Create a personal framework for conscious partnership and future alignment.',
      bullets: ['Everything in RITUAL','Define your long-term relationship vision','Create your conscious commitment framework','Prepare for future shared agreements'],
      closeLine: 'For those who want to lead with intention.',
      cta: 'Create My Vision',
    },
    couple: {
      title: 'Legacy Vision',
      tagline: 'Define your relationship \u2014 clearly, consciously, permanently.',
      bullets: ['Everything in RITUAL','Create your MarrsingClause\u00AE Agreement','Private responses locked before reveal','Certificate of Conscious Commitment','Unique Relationship ID'],
      closeLine: 'This is who you become as a couple.',
      cta: 'Create Our MarrsingClause\u00AE',
    },
  },
};
const TIERS_ORDER = ['CORE', 'RITUAL', 'LEGACY'];
const TIER_NAMES = { core: 'Core Clarity', ritual: 'Ritual Structure', legacy: 'Legacy Vision' };

/* ─── FAQ ─── */
const FAQ_ITEMS = [
  { q: 'Is MarrsingClause\u00AE therapy?', a: 'No. MarrsingClause\u00AE is a structured relational clarity system \u2014 not therapy, counseling, or medical guidance. It\u2019s a self-directed experience designed to help you and your partner articulate expectations, align on values, and build a conscious foundation together.' },
  { q: 'What happens after we pay?', a: 'You\u2019ll be guided through a short intake, then begin your reflection questions privately. If you\u2019re on the Couple path, your partner receives a secure invitation to complete their own reflections independently. Once both sides are locked in, you move into the reveal and alignment process.' },
  { q: 'Will my partner see my answers right away?', a: 'No. Each partner completes their reflections independently \u2014 responses are locked and invisible to the other person until both partners confirm they\u2019re ready. Privacy is built into every step.' },
  { q: 'What if we don\u2019t agree on everything?', a: 'That\u2019s expected and healthy. MarrsingClause\u00AE highlights areas of divergence alongside alignment, and provides guided conversation prompts to help you discuss differences productively. Disagreement isn\u2019t failure \u2014 it\u2019s the starting point of real understanding.' },
  { q: 'Is this legally binding?', a: 'No. MarrsingClause\u00AE does not create a legal contract, prenuptial agreement, or legally binding document. It\u2019s a personal commitment framework that documents your shared intentions and values \u2014 designed for clarity, not litigation.' },
  { q: 'Can we return and update our answers later?', a: 'You can review and edit your responses at any time before you lock them in. Once locked, your answers are sealed for the reveal process \u2014 ensuring both partners share honestly without influence.' },
  { q: 'Can I start alone and add my partner later?', a: 'Yes. The Individual path lets you begin your own clarity journey first. When you\u2019re ready, you can invite your partner to complete their part and move into the shared experience together.' },
];

/* ─── sub-components ─── */
const FaqItem = ({ item, isOpen, onToggle }) => (
  <div style={{ borderBottom: '1px solid #EDEAE6' }} data-testid={`faq-item-${item.q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between py-5 text-left gap-4" aria-expanded={isOpen} data-testid="faq-toggle">
      <span className="text-sm sm:text-base font-medium leading-snug" style={{ color: '#1A1A1A' }}>{item.q}</span>
      <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-300" style={{ color: '#9B9590', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
    </button>
    <div className="overflow-hidden transition-all duration-300 ease-in-out" style={{ maxHeight: isOpen ? '300px' : '0', opacity: isOpen ? 1 : 0 }}>
      <p className="text-sm leading-relaxed pb-5 pr-8" style={{ color: '#78716c' }}>{item.a}</p>
    </div>
  </div>
);

const RevealWrap = ({ children, delay = 0, className = '' }) => {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} className={className} style={{ opacity: vis ? 1 : 0, transform: vis ? 'translateY(0)' : 'translateY(18px)', transition: `opacity .5s cubic-bezier(.22,1,.36,1) ${delay}s, transform .5s cubic-bezier(.22,1,.36,1) ${delay}s` }}>
      {children}
    </div>
  );
};

const HOW_STEPS = [
  { icon: Layers, title: 'Choose Your Level', text: 'Select the experience that fits your relationship stage.' },
  { icon: PenLine, title: 'Reflect Independently', text: 'Each partner completes their responses privately.' },
  { icon: Users, title: 'Align Together', text: 'Reveal, discuss, and understand what matters most.' },
  { icon: Award, title: 'Receive Your Certificate', text: 'Get your Marrsing Alignment results and documented clarity.' },
];

/* ═══════════════════════════════ MAIN ═══════════════════════════════ */
const PlansPage = () => {
  const navigate = useNavigate();

  /* ── state ── */
  const [mode, setMode] = useState('couple');
  const [heroVis, setHeroVis] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [ctaPulse, setCtaPulse] = useState(false);
  const [savedTier, setSavedTier] = useState(null);     // from localStorage
  const [hoveredTier, setHoveredTier] = useState(null);  // for sticky bar
  const [showSticky, setShowSticky] = useState(false);
  const [stickyZone, setStickyZone] = useState('above'); // above | within | below

  /* refs for scroll tracking */
  const heroEndRef = useRef(null);
  const pricingRef = useRef(null);
  const pricingEndRef = useRef(null);
  const [finalRef, finalVis] = useReveal({ threshold: 0.3 });

  /* hover timer for persistence */
  const hoverTimer = useRef(null);

  /* ── init ── */
  useEffect(() => {
    const t = setTimeout(() => setHeroVis(true), 100);
    // restore saved selection
    const saved = loadSelection();
    if (saved) {
      setSavedTier(saved.tier);
      if (saved.mode) setMode(saved.mode);
    }
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (finalVis) { const t = setTimeout(() => setCtaPulse(true), 800); return () => clearTimeout(t); }
  }, [finalVis]);

  /* ── scroll tracking for sticky bar ── */
  useEffect(() => {
    const onScroll = () => {
      const heroBottom = heroEndRef.current?.getBoundingClientRect().bottom ?? 0;
      const pricingTop = pricingRef.current?.getBoundingClientRect().top ?? Infinity;
      const pricingBottom = pricingEndRef.current?.getBoundingClientRect().bottom ?? Infinity;

      setShowSticky(heroBottom < -20);

      if (pricingTop > window.innerHeight * 0.5) setStickyZone('above');
      else if (pricingBottom > 0) setStickyZone('within');
      else setStickyZone('below');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── handlers ── */
  const handleSelect = useCallback((tierKey) => {
    saveSelection(tierKey.toLowerCase(), mode);
    setSavedTier(tierKey.toLowerCase());
    navigate(`/start?mode=${mode}&tier=${tierKey.toLowerCase()}`);
  }, [mode, navigate]);

  const handleHoverStart = useCallback((tierKey) => {
    setHoveredTier(tierKey.toLowerCase());
    hoverTimer.current = setTimeout(() => {
      saveSelection(tierKey.toLowerCase(), mode);
      setSavedTier(tierKey.toLowerCase());
    }, 1800);
  }, [mode]);

  const handleHoverEnd = useCallback(() => {
    setHoveredTier(null);
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  }, []);

  const scrollToPricing = () => pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  /* ── sticky CTA text ── */
  const activeTier = hoveredTier || savedTier;
  const getStickyText = () => {
    if (stickyZone === 'above') return 'Choose Your Level';
    if (stickyZone === 'within' && activeTier && TIER_NAMES[activeTier])
      return `Continue with ${TIER_NAMES[activeTier]}`;
    if (stickyZone === 'within') return 'Start Now';
    return mode === 'couple' ? 'Begin Our Experience' : 'Begin with Clarity';
  };

  const handleStickyClick = () => {
    if (stickyZone === 'above') { scrollToPricing(); return; }
    if (activeTier) { navigate(`/start?mode=${mode}&tier=${activeTier}`); return; }
    scrollToPricing();
  };

  /* ── final CTA text ── */
  const finalCtaText = mode === 'couple' ? 'Begin Our Experience' : 'Begin with Clarity';

  return (
    <div className="min-h-screen" style={{ background: '#F7F5F2' }}>
      <style>{`
        @keyframes fadeSlideUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        @keyframes subtlePulse{0%,100%{box-shadow:0 0 0 0 rgba(26,26,26,0)}50%{box-shadow:0 0 0 8px rgba(26,26,26,.06)}}
        .tier-card-anim{opacity:0;animation:fadeSlideUp .55s cubic-bezier(.22,1,.36,1) forwards}
        .tier-card-anim:nth-child(1){animation-delay:.1s}.tier-card-anim:nth-child(2){animation-delay:.22s}.tier-card-anim:nth-child(3){animation-delay:.34s}
        .cta-pulse{animation:subtlePulse 2.5s ease-in-out infinite}
        .tier-highlight{box-shadow:0 0 0 2px #C9A84C !important}
      `}</style>

      <Header />

      {/* ═══ STICKY CTA BAR ═══ */}
      <div
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          opacity: showSticky ? 1 : 0,
          transform: showSticky ? 'translateY(0)' : 'translateY(-100%)',
          pointerEvents: showSticky ? 'auto' : 'none',
          background: 'rgba(247,245,242,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #E8E5E0',
        }}
        data-testid="sticky-cta-bar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <span className="text-sm font-medium hidden sm:block" style={{ color: '#1A1A1A' }}>MarrsingClause&reg;</span>
          <div className="flex items-center gap-3 mx-auto sm:mx-0">
            {savedTier && stickyZone !== 'above' && (
              <span className="text-xs text-stone-400 hidden sm:inline">Continue where you left off</span>
            )}
            <button
              onClick={handleStickyClick}
              className="rounded-full px-5 py-2 text-sm font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] flex items-center gap-1.5"
              style={{ background: '#2F9ED8', color: '#fff' }}
              data-testid="sticky-cta-button"
            >
              {getStickyText()}<ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-16 md:py-24">

        {/* ═══ 1. HERO ═══ */}
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 sm:mb-5 leading-tight" style={{ color: '#1A1A1A', opacity: heroVis?1:0, transform: heroVis?'translateY(0)':'translateY(16px)', transition:'all .7s cubic-bezier(.22,1,.36,1)' }} data-testid="plans-heading">
            Design Your Relationship<br className="hidden sm:block" /><span className="sm:hidden"> </span>with Clarity
          </h2>
          <p className="text-base sm:text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto px-2" style={{ opacity: heroVis?1:0, transform: heroVis?'translateY(0)':'translateY(14px)', transition:'all .7s cubic-bezier(.22,1,.36,1) .12s' }} data-testid="plans-subheadline">
            MarrsingClause&reg; is a structured commitment system that replaces assumptions with conscious agreement.
          </p>
          <p className="text-xs sm:text-sm text-stone-400 mt-4" style={{ opacity: heroVis?1:0, transition:'opacity .7s .24s' }} data-testid="plans-disclaimer">Not therapy. Not legal advice. Not a legal contract.</p>

          {/* Hero CTA */}
          <button
            onClick={scrollToPricing}
            className="mt-7 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
            style={{ background: '#2F9ED8', color: '#fff', opacity: heroVis?1:0, transition:'opacity .7s .36s' }}
            data-testid="hero-cta"
          >
            See the Levels<ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div ref={heroEndRef} />

        {/* ═══ 2. PROBLEM ═══ */}
        <RevealWrap className="max-w-2xl mx-auto mb-14 sm:mb-18 text-center">
          <p className="text-xs uppercase tracking-widest font-medium mb-4" style={{ color:'#9B9590' }} data-testid="problem-label">Why This Matters</p>
          <div className="space-y-3">
            {['Most couples never talk about what they actually expect from each other.','Assumptions build quietly \u2014 until they become the argument.','There\u2019s no safe, structured place to align before things get complicated.','Therapy starts after the problem. This starts before it.'].map((line,i)=>(
              <RevealWrap key={i} delay={i*.1}><p className="text-sm sm:text-base text-stone-600 leading-relaxed">{line}</p></RevealWrap>
            ))}
          </div>
        </RevealWrap>

        {/* ═══ 3. SOLUTION BRIDGE ═══ */}
        <RevealWrap className="max-w-2xl mx-auto mb-14 sm:mb-16 text-center">
          <p className="text-xs uppercase tracking-widest font-medium mb-3" style={{ color:'#9B9590' }} data-testid="solution-label">MarrsingClause Changes That</p>
          <p className="text-base sm:text-lg text-stone-700 leading-relaxed max-w-xl mx-auto">
            A guided experience that helps you and your partner reflect privately, align intentionally, and commit consciously {'\u2014'} before assumptions take root.
          </p>
        </RevealWrap>

        {/* ═══ 3.5 HOW IT WORKS ═══ */}
        <RevealWrap className="max-w-4xl mx-auto mb-14 sm:mb-18" data-testid="how-it-works-section">
          <h3 className="text-center text-lg sm:text-xl font-semibold mb-1" style={{ color:'#1A1A1A' }} data-testid="how-it-works-heading">How It Works</h3>
          <p className="text-center text-sm text-stone-500 mb-10">A simple, guided experience designed for clarity and alignment.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-6">
            {HOW_STEPS.map(({ icon: Ic, title, text }, i) => (
              <RevealWrap key={i} delay={i * 0.09} className="text-center">
                <div className="w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ border: '1px solid #E8E5E0' }}>
                  <Ic className="w-4.5 h-4.5" style={{ color: '#9B9590' }} />
                </div>
                <p className="text-xs uppercase tracking-widest font-medium mb-1" style={{ color: '#C9A84C' }}>Step {i + 1}</p>
                <p className="text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>{title}</p>
                <p className="text-xs text-stone-500 leading-relaxed max-w-[200px] mx-auto">{text}</p>
              </RevealWrap>
            ))}
          </div>

          <p className="text-center text-xs text-stone-400 mt-8">Designed to be completed at your own pace {'\u2014'} individually and together.</p>
        </RevealWrap>

        {/* mid-page CTA */}
        <RevealWrap className="text-center mb-10 sm:mb-14">
          <button onClick={scrollToPricing} className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]" style={{ background:'#2F9ED8', color:'#fff' }} data-testid="mid-cta">
            Choose Your Level<ArrowRight className="w-4 h-4" />
          </button>
        </RevealWrap>

        {/* ═══ 4. STEP 1 + TOGGLE ═══ */}
        <RevealWrap className="flex flex-col items-center mb-10 sm:mb-14">
          <p className="text-xs uppercase tracking-widest font-medium mb-2" style={{ color:'#C9A84C' }}>Step 1</p>
          <h3 className="text-lg sm:text-xl font-semibold mb-1" style={{ color:'#1A1A1A' }}>Choose your level of clarity</h3>
          <p className="text-sm text-stone-500 mb-6">Choose the level that fits your relationship journey.</p>
          <div className="inline-flex rounded-full p-1" style={{ background:'#E8E5E0' }} data-testid="mode-toggle">
            <button onClick={()=>{setMode('individual');if(savedTier)saveSelection(savedTier,'individual')}} className="px-5 sm:px-7 py-2.5 rounded-full text-sm sm:text-base font-medium transition-all duration-200" style={mode==='individual'?{background:'#2F9ED8',color:'#fff'}:{background:'transparent',color:'#6B6560'}} data-testid="toggle-individual">Individual</button>
            <button onClick={()=>{setMode('couple');if(savedTier)saveSelection(savedTier,'couple')}} className="px-5 sm:px-7 py-2.5 rounded-full text-sm sm:text-base font-medium transition-all duration-200" style={mode==='couple'?{background:'#2F9ED8',color:'#fff'}:{background:'transparent',color:'#6B6560'}} data-testid="toggle-couple">Couple</button>
          </div>
          <p className="text-xs sm:text-sm text-stone-500 mt-3 max-w-md text-center">Individual helps one person gain clarity first. Couple helps both partners move through the process together.</p>
        </RevealWrap>

        {/* ═══ 5. PRICING CARDS ═══ */}
        <div ref={pricingRef} className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8 max-w-6xl mx-auto items-stretch">
          {TIERS_ORDER.map((tierKey) => {
            const tier = TIER_DATA[tierKey]; const content = tier[mode]; const Icon = tier.icon;
            const isRitual = tierKey==='RITUAL'; const isLegacy = tierKey==='LEGACY';
            const isSaved = savedTier === tier.key;
            return (
              <div key={tierKey}
                className={`relative rounded-2xl flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl tier-card-anim ${isSaved ? 'tier-highlight' : ''}`}
                style={isLegacy?{background:'#1A1A1A',border:'1.5px solid #C9A84C',boxShadow:'0 4px 24px rgba(201,168,76,.10)'}:isRitual?{background:'#F0F5FA',border:'1.5px solid #C2D6EA',boxShadow:'0 4px 24px rgba(0,0,0,.06)'}:{background:'#FFFFFF',border:'1.5px solid #E8E5E0',boxShadow:'0 2px 12px rgba(0,0,0,.04)'}}
                data-testid={`tier-card-${tier.key}`}
                onMouseEnter={()=>handleHoverStart(tierKey)}
                onMouseLeave={handleHoverEnd}
              >
                {tier.badge && (<div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold tracking-wide whitespace-nowrap" style={isLegacy?{background:'#C9A84C',color:'#1A1A1A'}:{background:'#2F9ED8',color:'#fff'}} data-testid={`tier-badge-${tier.key}`}>{tier.badge}</div>)}
                <div className="p-6 sm:p-8 flex flex-col flex-1">
                  <div className="mb-5">
                    <Icon className="w-8 h-8 mb-3" style={{color:isLegacy?'#C9A84C':'#1A1A1A'}}/>
                    <h3 className="text-xl sm:text-2xl font-bold mb-1" style={{color:isLegacy?'#FFFFFF':'#1A1A1A'}} data-testid={`tier-title-${tier.key}`}>{content.title}</h3>
                    <p className="text-xs uppercase tracking-widest font-medium" style={{color:isLegacy?'#C9A84C':'#9B9590'}}>{tierKey}</p>
                  </div>
                  <p className="text-sm sm:text-base leading-relaxed mb-5" style={{color:isLegacy?'#D4D0CB':'#57534e'}} data-testid={`tier-tagline-${tier.key}`}>{content.tagline}</p>
                  <ul className="space-y-2.5 mb-5 flex-1">
                    {content.bullets.map((b,i)=>(<li key={i} className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color:isLegacy?'#C9A84C':isRitual?'#3B82F6':'#78716c'}}/><span className="text-sm leading-snug" style={{color:isLegacy?'#D4D0CB':'#57534e'}}>{b}</span></li>))}
                  </ul>
                  <p className="text-xs italic mb-2" style={{color:isLegacy?'#A09A92':'#9B9590'}} data-testid={`tier-close-${tier.key}`}>{content.closeLine}</p>
                  {content.audienceLine && <p className="text-xs mb-5" style={{color:isLegacy?'#8A847E':'#78716c'}} data-testid={`tier-audience-${tier.key}`}>{content.audienceLine}</p>}
                  {!content.audienceLine && <div className="mb-3"/>}
                  <div className="pt-5 border-t" style={{borderColor:isLegacy?'#333':'#E8E5E0'}}>
                    <div className="flex items-end gap-1 mb-4">
                      <span className="text-3xl sm:text-4xl font-bold" style={{color:isLegacy?'#FFFFFF':'#1A1A1A'}} data-testid={`tier-price-${tier.key}`}>${tier.price}</span>
                      <span className="text-sm mb-1" style={{color:isLegacy?'#A09A92':'#9B9590'}}>one-time</span>
                    </div>
                    <button onClick={()=>handleSelect(tierKey)} className="w-full rounded-full py-3 sm:py-3.5 text-sm sm:text-base font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] flex items-center justify-center gap-2" style={isLegacy?{background:'#C9A84C',color:'#1A1A1A'}:{background:'#2F9ED8',color:'#fff'}} data-testid={`tier-cta-${tier.key}`}>
                      {isSaved ? `Continue with ${content.title}` : content.cta}<ArrowRight className="w-4 h-4"/>
                    </button>
                    <p className="text-xs text-center mt-2.5" style={{color:isLegacy?'#8A847E':'#9B9590'}}>{tier.helper}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div ref={pricingEndRef} />

        <p className="text-center text-xs sm:text-sm text-stone-500 mt-6 max-w-lg mx-auto">Each level is designed to move you from reflection to greater clarity.</p>

        {/* ═══ 6. TRUST STRIP ═══ */}
        <RevealWrap className="max-w-4xl mx-auto mt-14 sm:mt-16" delay={0.05}>
          <div className="flex flex-col sm:flex-row items-center sm:items-start justify-center gap-6 sm:gap-0 sm:divide-x" style={{borderColor:'#E8E5E0'}} data-testid="trust-strip">
            {[
              {icon:Lock,title:'Private Experience',text:'Your responses are completed independently before being shared.'},
              {icon:Eye,title:'Guided Process',text:'A structured experience that walks you through each step with clarity.'},
              {icon:Award,title:'Certificate Included',text:'Each level includes a Marrsing Alignment Score with your results.'},
            ].map(({icon:Ic,title,text},i)=>(
              <div key={i} className="flex-1 text-center px-4 sm:px-8">
                <Ic className="w-5 h-5 mx-auto mb-2" style={{color:'#9B9590'}}/>
                <p className="text-sm font-medium mb-1" style={{color:'#1A1A1A'}}>{title}</p>
                <p className="text-xs text-stone-500 leading-relaxed max-w-[220px] mx-auto">{text}</p>
              </div>
            ))}
          </div>
        </RevealWrap>

        {/* ═══ 7. COMPARISON TABLE ═══ */}
        <RevealWrap className="max-w-5xl mx-auto mt-14 sm:mt-18" data-testid="comparison-table-section">
          <h3 className="text-center text-lg sm:text-xl font-semibold mb-6" style={{color:'#1A1A1A'}}>Compare at a Glance</h3>
          <div className="overflow-x-auto rounded-xl" style={{border:'1px solid #E8E5E0'}}>
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead><tr style={{background:'#EDEAE6'}}><th className="text-left py-3 px-4 sm:px-6 font-medium text-stone-600 w-1/4">Feature</th><th className="text-center py-3 px-3 sm:px-5 font-medium text-stone-600">Core</th><th className="text-center py-3 px-3 sm:px-5 font-medium text-stone-600">Ritual</th><th className="text-center py-3 px-3 sm:px-5 font-medium text-stone-600">Legacy</th></tr></thead>
              <tbody>
                {[{label:'Guided Self-Reflection',core:true,ritual:true,legacy:true},{label:'Marrsing Alignment Score',core:true,ritual:true,legacy:true},{label:'Clarity Conversation\u2122',core:false,ritual:true,legacy:true},{label:'Ongoing Check-in Rhythms',core:false,ritual:true,legacy:true},{label:'MarrsingClause\u00AE Agreement',core:false,ritual:false,legacy:true},{label:'Certificate of Commitment',core:false,ritual:false,legacy:true},{label:'Unique Relationship ID',core:false,ritual:false,legacy:true}].map((row,i)=>(
                  <tr key={i} style={{background:i%2===0?'#FFFFFF':'#FAFAF8',borderTop:'1px solid #F0EDE8'}}>
                    <td className="py-3 px-4 sm:px-6 text-stone-700 font-medium">{row.label}</td>
                    {['core','ritual','legacy'].map(t=>(<td key={t} className="py-3 px-3 sm:px-5 text-center">{row[t]?<Check className="w-4 h-4 mx-auto" style={{color:t==='legacy'?'#C9A84C':'#1A1A1A'}}/>:<span className="text-stone-300">{'\u2014'}</span>}</td>))}
                  </tr>
                ))}
                <tr style={{borderTop:'1.5px solid #E8E5E0',background:'#FAFAF8'}}><td className="py-4 px-4 sm:px-6 font-semibold text-stone-800">Price</td><td className="py-4 px-3 sm:px-5 text-center font-bold text-stone-900">$97</td><td className="py-4 px-3 sm:px-5 text-center font-bold text-stone-900">$197</td><td className="py-4 px-3 sm:px-5 text-center font-bold" style={{color:'#C9A84C'}}>$297</td></tr>
              </tbody>
            </table>
          </div>
        </RevealWrap>

        {/* ═══ 8. EMOTIONAL BRIDGE ═══ */}
        <RevealWrap className="max-w-2xl mx-auto mt-16 sm:mt-20 text-center" data-testid="emotional-bridge">
          <p className="text-base sm:text-lg leading-relaxed" style={{color:'#57534e'}}>
            This isn{'\u2019'}t about filling out answers.{' '}
            <span className="font-medium" style={{color:'#1A1A1A'}}>It{'\u2019'}s about creating the conversation your relationship has been waiting for.</span>
          </p>
        </RevealWrap>

        {/* ═══ 9. COST COMPARISON ═══ */}
        <RevealWrap className="max-w-4xl mx-auto mt-14 sm:mt-16" data-testid="cost-comparison-section">
          <h3 className="text-center text-lg sm:text-xl font-semibold mb-2" style={{color:'#1A1A1A'}}>A Valuable Investment in Your Relationship</h3>
          <p className="text-center text-sm text-stone-500 mb-8 max-w-xl mx-auto">MarrsingClause&reg; provides structured clarity at a fraction of the cost of traditional services.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            <div className="rounded-xl p-5 text-center" style={{background:'#FFFFFF',border:'1px solid #E8E5E0'}}><p className="text-xs uppercase tracking-widest text-stone-400 font-medium mb-2">Avg. Therapy Session</p><p className="text-2xl font-bold text-stone-800">$100 {'\u2013'} $250</p><p className="text-xs text-stone-400 mt-1">per session</p></div>
            <div className="rounded-xl p-5 text-center" style={{background:'#FFFFFF',border:'1px solid #E8E5E0'}}><p className="text-xs uppercase tracking-widest text-stone-400 font-medium mb-2">Avg. Attorney Fees</p><p className="text-2xl font-bold text-stone-800">$350 {'\u2013'} $500</p><p className="text-xs text-stone-400 mt-1">per hour</p></div>
            <div className="rounded-xl p-5 text-center" style={{background:'#1A1A1A',border:'1.5px solid #C9A84C'}}><p className="text-xs uppercase tracking-widest font-medium mb-2" style={{color:'#C9A84C'}}>MarrsingClause&reg;</p><p className="text-2xl font-bold text-white">$97 {'\u2013'} $297</p><p className="text-xs mt-1" style={{color:'#A09A92'}}>one-time, complete experience</p></div>
          </div>
          <p className="text-center text-xs text-stone-400 mt-5">MarrsingClause&reg; is not therapy, legal advice, or a legal contract. It is a structured relational clarity system.</p>
        </RevealWrap>

        {/* ═══ 10. IDENTITY ═══ */}
        <div className="max-w-2xl mx-auto mt-16 sm:mt-20 text-center" data-testid="identity-section">
          <RevealWrap><p className="text-xs uppercase tracking-widest font-medium mb-5" style={{color:'#9B9590'}}>For Couples Who Choose Clarity</p></RevealWrap>
          {['We don\u2019t assume. We ask.','We don\u2019t hope it works out. We build the foundation.','We don\u2019t wait for the problem. We design the solution.','We choose each other \u2014 consciously, clearly, and completely.'].map((line,i)=>(
            <RevealWrap key={i} delay={i*.1}><p className="text-base sm:text-lg font-medium leading-relaxed mb-3" style={{color:'#1A1A1A'}}>{line}</p></RevealWrap>
          ))}
        </div>

        {/* ═══ 11. FAQ ═══ */}
        <RevealWrap className="max-w-3xl mx-auto mt-16 sm:mt-20" data-testid="faq-section">
          <h3 className="text-center text-lg sm:text-xl font-semibold mb-1" style={{color:'#1A1A1A'}} data-testid="faq-heading">What to Know Before You Start</h3>
          <p className="text-center text-sm text-stone-500 mb-8">A few important things to know before you begin</p>
          <div className="rounded-xl px-5 sm:px-7" style={{background:'#FFFFFF',border:'1px solid #EDEAE6'}}>
            {FAQ_ITEMS.map((item,i)=>(<FaqItem key={i} item={item} isOpen={openFaq===i} onToggle={()=>setOpenFaq(openFaq===i?null:i)}/>))}
          </div>
        </RevealWrap>

        {/* ═══ 12. FINAL CTA ═══ */}
        <div ref={finalRef} className="max-w-2xl mx-auto mt-20 sm:mt-24 mb-8 text-center" data-testid="final-cta-section" style={{opacity:finalVis?1:0,transform:finalVis?'translateY(0)':'translateY(20px)',transition:'all .6s cubic-bezier(.22,1,.36,1) .15s'}}>
          <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{color:'#1A1A1A'}}>You don{'\u2019'}t have to guess anymore.</h3>
          <p className="text-sm sm:text-base text-stone-600 mb-8 max-w-md mx-auto">Start with clarity. Continue with intention.</p>
          <button onClick={()=>{ if(savedTier){navigate(`/start?mode=${mode}&tier=${savedTier}`)}else{scrollToPricing()} }} className={`inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-semibold transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] ${ctaPulse?'cta-pulse':''}`} style={{background:'#2F9ED8',color:'#fff'}} data-testid="final-cta-button">
            {savedTier ? `Continue with ${TIER_NAMES[savedTier]}` : finalCtaText}<ArrowRight className="w-4 h-4"/>
          </button>
          <p className="text-xs mt-5" style={{color:'#9B9590'}}>Private. Guided. Meaningful.</p>
        </div>
      </div>
    </div>
  );
};

export default PlansPage;
